import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building2, Ruler, ChevronRight } from 'lucide-react';
import { getDisplayPrice, formatPrice } from '../../../utils/priceCalculator';

const PROPERTY_TYPE_TRANSLATIONS = {
  'house': 'Maison',
  'apartment': 'Appartement',
  'building': 'Immeuble'
} as const;

const RENOVATION_LEVEL_TRANSLATIONS = {
  'light': 'Légère',
  'medium': 'Moyenne',
  'heavy': 'Lourde',
} as const;

const KEY_ELEMENTS_TRANSLATIONS = {
  'windows': 'Changement des fenêtres',
  'kitchen': 'Rénovation de la cuisine',
  'bathroom': 'Rénovation de la salle de bain'
} as const;

interface QuickEstimateViewProps {
  data: any;
  onBack: () => void;
}

export const QuickEstimateView: React.FC<QuickEstimateViewProps> = ({ data, onBack }) => {
  const navigate = useNavigate();

  const translatePropertyType = (type: string) => {
    if (!type) return 'Non renseigné';
    const key = type?.toLowerCase() as keyof typeof PROPERTY_TYPE_TRANSLATIONS;
    return PROPERTY_TYPE_TRANSLATIONS[key] || type;
  };

  const translateRenovationLevel = (level: string) => {
    if (!level) return 'Non renseigné';
    const key = level?.toLowerCase() as keyof typeof RENOVATION_LEVEL_TRANSLATIONS;
    return RENOVATION_LEVEL_TRANSLATIONS[key] || level;
  };

  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Aucune donnée d'estimation disponible</p>
        <button onClick={onBack} className="mt-4 btn btn-secondary">
          Retour
        </button>
      </div>
    );
  }

  console.log('📋 QuickEstimateView - Data received:', {
    type: data.type,
    hasPropertyType: !!data.propertyType,
    propertyType: data.propertyType,
    hasArea: !!data.area,
    area: data.area,
    hasRenovationLevel: !!data.renovationLevel,
    renovationLevel: data.renovationLevel,
    hasPriceRange: !!data.priceRange,
    priceRange: data.priceRange,
    hasLocation: !!data.location,
    location: data.location
  });

  const priceResult = getDisplayPrice(data);
  const hasRange = data.priceRange?.min && data.priceRange?.max && data.priceRange.min !== data.priceRange.max;

  console.log('💰 Price calculation result:', priceResult);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-bold text-neutral-800">Estimation rapide</h1>
        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
          Estimation rapide
        </span>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-neutral-800">Informations du bien</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-neutral-400" />
            <span className="text-neutral-600">
              {data.location?.address || data.address || 'Non renseigné'}
              {data.location?.postcode && ` ${data.location.postcode}`}
              {data.location?.city && ` ${data.location.city}`}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Building2 className="h-4 w-4 text-neutral-400" />
            <span className="text-neutral-600">
              {translatePropertyType(data.propertyType)}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Ruler className="h-4 w-4 text-neutral-400" />
            <span className="text-neutral-600">
              {data.area ? `${data.area} m²` : 'Non renseigné'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
        <h3 className="text-base font-bold text-blue-900 mb-4">Estimation des coûts</h3>

        <div className="mb-4">
          <p className="text-sm text-neutral-700 mb-2">Le coût estimé des travaux se situe entre :</p>
          {priceResult.displayPrice > 0 ? (
            <p className="text-2xl font-bold text-blue-900">
              {hasRange
                ? `${formatPrice(data.priceRange.min)} - ${formatPrice(data.priceRange.max)}`
                : formatPrice(priceResult.displayPrice)
              }
            </p>
          ) : (
            <p className="text-xl text-orange-700">
              Prix non disponible - Données incomplètes
            </p>
          )}
        </div>

        {priceResult.isRecalculated && (
          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800 font-medium">
              ⚠️ Ce prix a été recalculé avec les tarifs actuels.
            </p>
            {priceResult.originalPrice && priceResult.percentageDifference !== undefined && Math.abs(priceResult.percentageDifference) > 5 && (
              <p className="text-xs text-orange-700 mt-1">
                Prix original : {formatPrice(priceResult.originalPrice)} (différence : {priceResult.percentageDifference > 0 ? '+' : ''}{priceResult.percentageDifference.toFixed(1)}%)
              </p>
            )}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-blue-200">
          <p className="text-sm text-blue-800 leading-relaxed">
            Les prix dans SimulReno sont basés sur le marché à début 2026. Ils sont volontairement surévalués d'environ 10%. Il est toujours préférable de provisionner une marge pour les aléas lorsque l'on se lance dans une rénovation.
          </p>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-neutral-800 mb-4">Détails de la rénovation</h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-neutral-100">
            <span className="text-neutral-600">Niveau de rénovation</span>
            <span className="font-medium text-neutral-800">
              {translateRenovationLevel(data.renovationLevel)}
            </span>
          </div>

          {(data.keyElements?.windows || data.keyElements?.kitchen || data.keyElements?.bathroom) && (
            <div className="pt-2">
              <p className="text-sm font-medium text-neutral-700 mb-2">Éléments clés inclus :</p>
              <div className="space-y-2">
                {data.keyElements?.windows && (
                  <div className="flex items-center space-x-2 text-sm text-neutral-600">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    <span>{KEY_ELEMENTS_TRANSLATIONS.windows}</span>
                  </div>
                )}
                {data.keyElements?.kitchen && (
                  <div className="flex items-center space-x-2 text-sm text-neutral-600">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    <span>{KEY_ELEMENTS_TRANSLATIONS.kitchen}</span>
                  </div>
                )}
                {data.keyElements?.bathroom && (
                  <div className="flex items-center space-x-2 text-sm text-neutral-600">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    <span>{KEY_ELEMENTS_TRANSLATIONS.bathroom}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-orange-500 text-white rounded-lg p-6 shadow-lg">
        <h3 className="text-base font-semibold mb-2">Besoin d'une estimation plus précise ?</h3>
        <p className="text-sm mb-4">
          Obtenez une estimation détaillée prenant en compte tous les aspects de votre projet de rénovation
        </p>
        <button
          onClick={() => navigate('/estimation/nouvelle')}
          className="w-full bg-white text-orange-500 py-3 px-6 rounded-lg font-medium hover:bg-orange-50 transition-colors"
        >
          Obtenir une estimation détaillée
        </button>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onBack}
          className="btn btn-secondary"
        >
          Retour au tableau de bord
        </button>
      </div>
    </div>
  );
};

export default QuickEstimateView;
